import type { BillingProcess } from '@policyvision/contracts';
import type { ParseKeys } from 'i18next';
import { z } from 'zod';

import type { BillingSummary } from '../api/FetchBillingList';

type NamingToken = {
  label?: ParseKeys;
  schema: z.ZodType<string, string>;
  defaultValue?: (billing: BillingSummary, process: BillingProcess) => string;
};

const baseSchema = z
  .string()
  .trim()
  .min(1, 'billing.drawer.rename.completeRequired');

const typeSchemas = {
  text: baseSchema,
  date: baseSchema.pipe(z.iso.date('billing.drawer.rename.invalidDate')),
  amount: baseSchema
    .pipe(
      z
        .string()
        .regex(
          /^(?!-0([.,]0+)?$)-?(0|[1-9]\d*)([.,]\d{1,2})?$/,
          'billing.drawer.rename.invalidAmount',
        ),
    )
    .transform((amount) => amount.replace(',', '.'))
    .transform((amount) =>
      amount.startsWith('-') ? `(${amount.slice(1)}$)` : `${amount}$`,
    ),
};

const NAMING_TOKENS = new Map<string, NamingToken>([
  [
    'processus',
    {
      label: 'billing.drawer.rename.tokens.processus',
      schema: typeSchemas.text,
      defaultValue: (_, process) => process.label,
    },
  ],
  [
    'date',
    {
      label: 'billing.drawer.rename.tokens.date',
      schema: typeSchemas.date,
      defaultValue: (billing) => billing.effectiveDate.slice(0, 10),
    },
  ],
  [
    'dateFin',
    {
      label: 'billing.drawer.rename.tokens.dateFin',
      schema: typeSchemas.date,
      defaultValue: (billing) => billing.expiryDate.slice(0, 10),
    },
  ],
  [
    'dateSinistre',
    {
      label: 'billing.drawer.rename.tokens.dateSinistre',
      schema: typeSchemas.date,
    },
  ],
  [
    'prime',
    { label: 'billing.drawer.rename.tokens.prime', schema: typeSchemas.amount },
  ],
  [
    'montant',
    {
      label: 'billing.drawer.rename.tokens.montant',
      schema: typeSchemas.amount,
    },
  ],
  [
    'description',
    {
      label: 'billing.drawer.rename.tokens.description',
      schema: typeSchemas.text,
    },
  ],
  [
    'participant',
    {
      label: 'billing.drawer.rename.tokens.participant',
      schema: typeSchemas.text,
    },
  ],
  [
    'projet',
    { label: 'billing.drawer.rename.tokens.projet', schema: typeSchemas.text },
  ],
  [
    'expert',
    { label: 'billing.drawer.rename.tokens.expert', schema: typeSchemas.text },
  ],
]);

export const getNamingToken = (tokenKey: string): NamingToken => {
  return NAMING_TOKENS.get(tokenKey) ?? { schema: typeSchemas.text };
};
